import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  TextField,
  Select,
  MenuItem,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { Briefcase, MapPin, Building2, Plus, ExternalLink, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import axios from "axios";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import EmptyState from "./EmptyState";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

const COLUMN_MIN_WIDTH = 240;

const DropIndicator = () => (
  <Box
    sx={{
      height: "2px",
      bgcolor: "hsl(153, 68%, 40%)",
      borderRadius: "1px",
      mx: "4px",
      my: "-5px",
      position: "relative",
      zIndex: 2,
      "&::before": {
        content: "\"\"",
        position: "absolute",
        left: -3,
        top: -3,
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: "hsl(153, 68%, 40%)",
      },
    }}
  />
);

const SAVED_COLUMN_KEY = "saved";

const canCrossColumn = (sourceColumnKey, targetColumnKey) => {
  if (sourceColumnKey === SAVED_COLUMN_KEY && targetColumnKey !== SAVED_COLUMN_KEY) return false;
  if (targetColumnKey === SAVED_COLUMN_KEY && sourceColumnKey !== SAVED_COLUMN_KEY) return false;
  return true;
};

const SOURCE_OPTIONS = ["linkedin", "indeed", "company_website", "referral", "other"];

const CLOSED_PAGE_SIZE = 6;

// Colors for the JobApplication status badge. Closed is the only terminal/negative
// state, so it is the only one that reads as muted rather than green.
const APPLICATION_STATUS_COLORS = {
  pending: { color: "hsl(38, 80%, 38%)", bgcolor: "hsla(38, 80%, 50%, 0.12)" },
  open: { color: "hsl(153, 68%, 25%)", bgcolor: "hsla(153, 68%, 25%, 0.08)" },
  saved: { color: "hsl(178, 10%, 45%)", bgcolor: "hsla(160, 10%, 96%, 0.8)" },
  accepted: { color: "hsl(153, 68%, 25%)", bgcolor: "hsla(153, 68%, 25%, 0.08)" },
  closed: { color: "hsl(0, 55%, 45%)", bgcolor: "hsla(0, 55%, 50%, 0.1)" },
};

const applicationStatusStyle = (status) =>
  APPLICATION_STATUS_COLORS[status] || APPLICATION_STATUS_COLORS.saved;

const ExternalAppModal = ({ open, onClose, onSubmit, columns, initialData, mode }) => {
  const intl = useIntl();
  const isMobile = useMediaQuery("(max-width:599px)");

  const [form, setForm] = useState({
    company_name: "",
    job_title: "",
    url: "",
    location: "",
    source: "",
    kanban_column_key: "applied",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      setForm({
        company_name: initialData.company_name || "",
        job_title: initialData.job_title || "",
        url: initialData.url || "",
        location: initialData.location || "",
        source: initialData.source || "",
        kanban_column_key: initialData.kanban_column_key || "applied",
        notes: initialData.notes || "",
      });
    } else if (open) {
      setForm({ company_name: "", job_title: "", url: "", location: "", source: "", kanban_column_key: "applied", notes: "" });
    }
  }, [open, initialData]);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(form);
      onClose();
    } catch {
      // The parent already told the user; keep the modal open so the input isn't lost.
    } finally {
      setSubmitting(false);
    }
  };

  const nonSavedColumns = columns.filter((c) => c.key !== SAVED_COLUMN_KEY);
  const isEdit = mode === "edit";
  const canSubmit = form.company_name.trim() && form.job_title.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          p: "20px 24px",
          borderBottom: "1px solid hsla(160, 10%, 90%, 0.6)",
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            bgcolor: "hsla(153, 68%, 25%, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isEdit
            ? <Pencil size={18} color="hsl(153, 68%, 25%)" />
            : <Plus size={18} color="hsl(153, 68%, 25%)" />}
        </Box>
        <Typography
          sx={{
            flex: 1,
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            fontSize: "16px",
            color: "hsl(178, 93%, 10%)",
          }}
        >
          {intl.formatMessage({ id: isEdit ? "candidate.kanban.external_form.edit_title" : "candidate.kanban.external_form.title", defaultMessage: isEdit ? "Edit application" : "Add external application" })}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "hsl(178, 10%, 45%)" }}>
          <X size={18} />
        </IconButton>
      </Box>

      {/* Fields */}
      <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: "16px", overflowY: "auto" }}>
        <Box>
          <Typography
            component="label"
            sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
          >
            {intl.formatMessage({ id: "candidate.kanban.external_form.company_name", defaultMessage: "Company name" })}
            <Box component="span" sx={{ color: "hsl(0, 65%, 51%)", ml: "2px" }}>*</Box>
          </Typography>
          <TextField
            value={form.company_name}
            onChange={handleChange("company_name")}
            fullWidth
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                height: "44px",
                borderRadius: "12px",
                bgcolor: "hsla(160, 10%, 96%, 0.3)",
                fontSize: "13px",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(178, 93%, 10%)",
                transition: "border-color 0.15s",
                "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
              },
              "& .MuiOutlinedInput-input": { px: "12px", py: 0 },
            }}
          />
        </Box>

        <Box>
          <Typography
            component="label"
            sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
          >
            {intl.formatMessage({ id: "candidate.kanban.external_form.job_title", defaultMessage: "Job title" })}
            <Box component="span" sx={{ color: "hsl(0, 65%, 51%)", ml: "2px" }}>*</Box>
          </Typography>
          <TextField
            value={form.job_title}
            onChange={handleChange("job_title")}
            fullWidth
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                height: "44px",
                borderRadius: "12px",
                bgcolor: "hsla(160, 10%, 96%, 0.3)",
                fontSize: "13px",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(178, 93%, 10%)",
                transition: "border-color 0.15s",
                "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
              },
              "& .MuiOutlinedInput-input": { px: "12px", py: 0 },
            }}
          />
        </Box>

        <Box>
          <Typography
            component="label"
            sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
          >
            {intl.formatMessage({ id: "candidate.kanban.external_form.url", defaultMessage: "Job posting URL" })}
          </Typography>
          <TextField
            value={form.url}
            onChange={handleChange("url")}
            fullWidth
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                height: "44px",
                borderRadius: "12px",
                bgcolor: "hsla(160, 10%, 96%, 0.3)",
                fontSize: "13px",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(178, 93%, 10%)",
                transition: "border-color 0.15s",
                "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
              },
              "& .MuiOutlinedInput-input": { px: "12px", py: 0 },
            }}
          />
        </Box>

        {/* Location + Source share a row: both are short, optional metadata */}
        <Box sx={{ display: "flex", gap: "12px", flexDirection: { xs: "column", sm: "row" } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="label"
              sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
            >
              {intl.formatMessage({ id: "candidate.kanban.external_form.location", defaultMessage: "Location" })}
            </Typography>
            <TextField
              value={form.location}
              onChange={handleChange("location")}
              fullWidth
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                  borderRadius: "12px",
                  bgcolor: "hsla(160, 10%, 96%, 0.3)",
                  fontSize: "13px",
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(178, 93%, 10%)",
                  transition: "border-color 0.15s",
                  "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                  "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                  "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
                },
                "& .MuiOutlinedInput-input": { px: "12px", py: 0 },
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="label"
              sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
            >
              {intl.formatMessage({ id: "candidate.kanban.external_form.source", defaultMessage: "Source" })}
            </Typography>
            <Select
              value={form.source}
              onChange={handleChange("source")}
              displayEmpty
              fullWidth
              sx={{
                height: "44px",
                borderRadius: "12px",
                bgcolor: "hsla(160, 10%, 96%, 0.3)",
                fontSize: "13px",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(178, 93%, 10%)",
                transition: "border-color 0.15s",
                "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
                "& .MuiSelect-select": { px: "12px", display: "flex", alignItems: "center" },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    mt: "4px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    border: "1px solid hsla(160, 10%, 90%, 0.6)",
                    "& .MuiMenuItem-root": {
                      fontSize: "13px",
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(178, 93%, 10%)",
                      "&.Mui-selected": { bgcolor: "hsla(153, 68%, 25%, 0.08)" },
                      "&.Mui-selected:hover": { bgcolor: "hsla(153, 68%, 25%, 0.12)" },
                    },
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box component="span" sx={{ color: "hsl(178, 10%, 55%)" }}>None</Box>
              </MenuItem>
              {SOURCE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {intl.formatMessage({ id: `candidate.kanban.external_form.source_options.${s}`, defaultMessage: s })}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {!isEdit && (
          <Box>
            <Typography
              component="label"
              sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
            >
              {intl.formatMessage({ id: "candidate.kanban.external_form.column", defaultMessage: "Stage" })}
            </Typography>
            <Select
              value={form.kanban_column_key}
              onChange={handleChange("kanban_column_key")}
              fullWidth
              sx={{
                height: "44px",
                borderRadius: "12px",
                bgcolor: "hsla(160, 10%, 96%, 0.3)",
                fontSize: "13px",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(178, 93%, 10%)",
                transition: "border-color 0.15s",
                "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
                "& .MuiSelect-select": { px: "12px", display: "flex", alignItems: "center" },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    mt: "4px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    border: "1px solid hsla(160, 10%, 90%, 0.6)",
                    "& .MuiMenuItem-root": {
                      fontSize: "13px",
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(178, 93%, 10%)",
                      "&.Mui-selected": { bgcolor: "hsla(153, 68%, 25%, 0.08)" },
                      "&.Mui-selected:hover": { bgcolor: "hsla(153, 68%, 25%, 0.12)" },
                    },
                  },
                },
              }}
            >
              {nonSavedColumns.map((col) => (
                <MenuItem key={col.key} value={col.key}>
                  {intl.formatMessage({ id: `candidate.kanban.${col.key}`, defaultMessage: col.name })}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {isEdit && (
          <Box>
            <Typography
              component="label"
              sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px", display: "block" }}
            >
              {intl.formatMessage({ id: "candidate.kanban.external_form.notes", defaultMessage: "Notes" })}
            </Typography>
            <TextField
              value={form.notes}
              onChange={handleChange("notes")}
              fullWidth
              multiline
              rows={3}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: "auto",
                  py: "10px",
                  borderRadius: "12px",
                  bgcolor: "hsla(160, 10%, 96%, 0.3)",
                  fontSize: "13px",
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(178, 93%, 10%)",
                  transition: "border-color 0.15s",
                  "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                  "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
                  "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
                },
                "& .MuiOutlinedInput-input": { px: "12px", py: 0 },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          p: "16px 24px",
          borderTop: "1px solid hsla(160, 10%, 90%, 0.6)",
          bgcolor: "hsla(160, 10%, 96%, 0.3)",
        }}
      >
        <Box
          component="button"
          onClick={onClose}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: "20px",
            py: "10px",
            borderRadius: "12px",
            border: "1px solid hsla(160, 10%, 90%, 0.6)",
            bgcolor: "hsl(0, 0%, 100%)",
            cursor: "pointer",
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            fontSize: "12px",
            color: "hsl(178, 10%, 45%)",
            transition: "all 0.15s",
            "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.8)" },
          }}
        >
          {intl.formatMessage({ id: "candidate.kanban.external_form.cancel", defaultMessage: "Cancel" })}
        </Box>
        <Box
          component="button"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            px: "20px",
            py: "10px",
            borderRadius: "12px",
            border: "none",
            bgcolor: "hsl(153, 68%, 25%)",
            color: "hsl(0, 0%, 100%)",
            cursor: "pointer",
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            fontSize: "12px",
            transition: "all 0.15s",
            "&:hover": { bgcolor: "hsl(153, 68%, 20%)" },
            "&:disabled": { bgcolor: "hsla(153, 68%, 25%, 0.35)", cursor: "not-allowed" },
          }}
        >
          {submitting && <CircularProgress size={12} sx={{ color: "hsl(0, 0%, 100%)" }} />}
          {intl.formatMessage({ id: isEdit ? "candidate.kanban.external_form.save" : "candidate.kanban.external_form.submit", defaultMessage: isEdit ? "Save" : "Add" })}
        </Box>
      </Box>
    </Dialog>
  );
};

const KanbanCardItem = ({
  card,
  columnId,
  columnKey,
  index,
  draggingCardId,
  dropEdge,
  onEditExternal,
  onDeleteExternal,
  readOnly = false,
}) => {
  const ref = useRef(null);
  const intl = useIntl();

  const isExternal = card.subject_type === "ExternalJobApplication";
  const extData = card.external_job_application;
  const offer = card.job_offer;
  const company = offer?.company;

  // A closed application keeps its card and its column: it is shown as a finished,
  // non-draggable entry instead of silently disappearing from the board.
  const isClosed = card.active === false;
  const isOfferClosed = !isExternal && offer?.offer_status === "closed";

  const displayTitle = isExternal ? extData?.job_title : offer?.job_title;
  const displayCompany = isExternal ? extData?.company_name : company?.name;
  const displayLocation = isExternal
    ? extData?.location
    : offer?.locations?.map((l) => l.city || l.region).filter(Boolean).join(", ");

  useEffect(() => {
    const el = ref.current;
    if (!el || readOnly) return undefined;

    const behaviours = [];

    if (!isClosed) {
      behaviours.push(
        draggable({
          element: el,
          getInitialData: () => ({
            type: "kanban-card",
            cardId: card.id,
            fromColumnId: Number(columnId),
            fromColumnKey: columnKey,
          }),
        }),
      );
    }

    behaviours.push(
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => {
          if (source?.data?.type !== "kanban-card") return false;
          return canCrossColumn(source.data.fromColumnKey, columnKey);
        },
        getData: ({ input, element }) =>
          attachClosestEdge(
            { type: "kanban-card-target", columnId: Number(columnId), columnKey, index },
            { input, element, allowedEdges: ["top", "bottom"] },
          ),
      }),
    );

    return combine(...behaviours);
  }, [card.id, columnId, columnKey, index, isClosed, readOnly]);

  return (
    <>
      {dropEdge === "top" && <DropIndicator />}
      <Box
        ref={ref}
        sx={{
          bgcolor: isClosed ? "hsla(160, 10%, 96%, 0.5)" : "hsl(0, 0%, 100%)",
          borderRadius: "12px",
          border: isClosed
            ? "1px solid hsla(160, 10%, 85%, 0.8)"
            : isExternal
              ? "1px solid hsla(220, 60%, 70%, 0.4)"
              : "1px solid hsla(160, 10%, 90%, 0.6)",
          p: "14px",
          cursor: isClosed ? "default" : "grab",
          userSelect: "none",
          opacity: draggingCardId === card.id ? 0.5 : isClosed ? 0.75 : 1,
          transition: "box-shadow 0.15s, opacity 0.15s",
          position: "relative",
          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
          "&:hover .external-actions": { opacity: 1 },
        }}
      >
        {/* Edit/delete actions for external cards */}
        {isExternal && (
          <Box
            className="external-actions"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              display: "flex",
              gap: "2px",
              opacity: 0,
              transition: "opacity 0.15s",
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEditExternal?.(card); }}
              sx={{ p: "3px" }}
            >
              <Pencil size={12} style={{ color: "hsl(178, 10%, 45%)" }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteExternal?.(card); }}
              sx={{ p: "3px" }}
            >
              <Trash2 size={12} style={{ color: "hsl(0, 60%, 50%)" }} />
            </IconButton>
          </Box>
        )}

        {/* Company logo/icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: isExternal ? "hsla(220, 60%, 50%, 0.08)" : "hsla(153, 68%, 25%, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden",
              border: "1px solid hsla(160, 10%, 90%, 0.5)",
            }}
          >
            {!isExternal && company?.logo_url ? (
              <Box
                component="img"
                src={company.logo_url}
                alt={company?.name}
                loading="lazy"
                sx={{ width: 32, height: 32, objectFit: "contain" }}
              />
            ) : isExternal ? (
              <ExternalLink size={14} style={{ color: "hsl(220, 60%, 50%)" }} />
            ) : (
              <Building2 size={14} style={{ color: "hsl(153, 68%, 25%)" }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "12px",
                color: "hsl(178, 93%, 10%)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayTitle || "Untitled"}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "hsl(178, 10%, 45%)",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayCompany}
            </Typography>
          </Box>
        </Box>

        {/* Location */}
        {displayLocation && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: "8px", flexWrap: "wrap" }}>
            <MapPin size={10} style={{ color: "hsl(178, 10%, 55%)", flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: "10px",
                color: "hsl(178, 10%, 55%)",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              }}
            >
              {displayLocation}
            </Typography>
          </Box>
        )}

        {/* Badges */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "8px" }}>
          {isExternal && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "hsl(220, 60%, 50%)",
                bgcolor: "hsla(220, 60%, 50%, 0.08)",
                borderRadius: "9999px",
                px: "8px",
                py: "2px",
              }}
            >
              <ExternalLink size={10} />
              {intl.formatMessage({ id: "candidate.kanban.external_badge", defaultMessage: "External" })}
            </Box>
          )}

          {isExternal && extData?.source && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: "10px",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(178, 10%, 45%)",
                bgcolor: "hsla(160, 10%, 96%, 0.8)",
                borderRadius: "9999px",
                px: "8px",
                py: "2px",
              }}
            >
              {intl.formatMessage({ id: `candidate.kanban.external_form.source_options.${extData.source}`, defaultMessage: extData.source })}
            </Box>
          )}

          {card.subject_type === "JobApplication" && card.job_application && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                borderRadius: "9999px",
                px: "8px",
                py: "2px",
                ...applicationStatusStyle(card.job_application.status),
              }}
            >
              {isClosed ? <X size={10} /> : <Briefcase size={10} />}
              {intl.formatMessage({
                id: `candidate.kanban.application_status.${card.job_application.status}`,
                defaultMessage: card.job_application.status,
              })}
            </Box>
          )}

          {/* The offer itself was taken offline; the application (if any) still stands */}
          {isOfferClosed && !isClosed && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "hsl(178, 10%, 45%)",
                bgcolor: "hsla(160, 10%, 90%, 0.8)",
                borderRadius: "9999px",
                px: "8px",
                py: "2px",
              }}
            >
              <AlertTriangle size={10} />
              {intl.formatMessage({
                id: "candidate.kanban.offer_closed",
                defaultMessage: "Offer closed",
              })}
            </Box>
          )}
        </Box>


      </Box>
      {dropEdge === "bottom" && <DropIndicator />}
    </>
  );
};

const KanbanColumnView = ({ column, cards, draggingCardId, cardDropTarget, onEditExternal, onDeleteExternal }) => {
  const intl = useIntl();
  const ref = useRef(null);
  const count = cards?.length || 0;

  const isDropTarget =
    cardDropTarget && String(cardDropTarget.columnId) === String(column.id);

  const showBottomIndicator =
    isDropTarget &&
    cardDropTarget.type === "column";

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    return dropTargetForElements({
      element: el,
      canDrop: ({ source }) => {
        if (source?.data?.type !== "kanban-card") return false;
        return canCrossColumn(source.data.fromColumnKey, column.key);
      },
      getData: () => ({ type: "column", columnId: Number(column.id), columnKey: column.key }),
    });
  }, [column.id, column.key]);

  const getDropEdge = (idx) => {
    if (!cardDropTarget || String(cardDropTarget.columnId) !== String(column.id)) return null;
    if (cardDropTarget.type !== "card") return null;
    if (cardDropTarget.index === idx && cardDropTarget.edge === "top") return "top";
    if (cardDropTarget.index === idx && cardDropTarget.edge === "bottom") return "bottom";
    return null;
  };

  const i18nKey = `candidate.kanban.${column.key}`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: COLUMN_MIN_WIDTH,
        flex: "1 1 0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mb: "12px",
          px: "4px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            fontSize: "12px",
            color: "hsl(178, 93%, 10%)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {intl.formatMessage({ id: i18nKey, defaultMessage: column.name })}
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: "10px",
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            color: "hsl(178, 10%, 55%)",
            bgcolor: "hsla(160, 10%, 96%, 0.8)",
            borderRadius: "9999px",
            px: "7px",
            py: "1px",
          }}
        >
          {count}
        </Box>
      </Box>

      {/* Cards list: generous padding for easier drops */}
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flex: 1,
          minHeight: 120,
          p: "6px",
          borderRadius: "12px",
          bgcolor: isDropTarget ? "hsla(153, 68%, 25%, 0.05)" : "transparent",
          border: isDropTarget
            ? "2px dashed hsla(153, 68%, 40%, 0.35)"
            : "2px dashed transparent",
          transition: "background 0.15s, border-color 0.15s",
        }}
      >
        {(cards || []).map((card, idx) => (
          <KanbanCardItem
            key={card.id}
            card={card}
            columnId={column.id}
            columnKey={column.key}
            index={idx}
            draggingCardId={draggingCardId}
            dropEdge={getDropEdge(idx)}
            onEditExternal={onEditExternal}
            onDeleteExternal={onDeleteExternal}
          />
        ))}

        {showBottomIndicator && <DropIndicator />}

        {count === 0 && !isDropTarget && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              minHeight: 60,
              borderRadius: "10px",
              border: "1px dashed hsla(160, 10%, 85%, 0.6)",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                color: "hsla(178, 10%, 55%, 0.5)",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              }}
            >
              None
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Closed applications live below the board rather than inside the columns, mirroring
// the separation the recruiter dashboard uses for its closed candidates.
const ClosedApplicationsSection = ({ cards }) => {
  const intl = useIntl();
  const [limit, setLimit] = useState(CLOSED_PAGE_SIZE);

  if (!cards.length) return null;

  return (
    <Box
      sx={{
        mt: "32px",
        p: "16px",
        borderRadius: "12px",
        border: "1px solid hsla(160, 10%, 90%, 0.8)",
        bgcolor: "hsla(160, 10%, 96%, 0.35)",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Typography
          sx={{
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            fontSize: "13px",
            color: "hsl(178, 93%, 10%)",
          }}
        >
          {intl.formatMessage({ id: "candidate.kanban.closed_section_title", defaultMessage: "Closed applications" })}
        </Typography>
        <Box
          sx={{
            fontSize: "11px",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(178, 10%, 45%)",
            bgcolor: "hsla(160, 10%, 88%, 0.9)",
            borderRadius: "6px",
            px: "6px",
            py: "1px",
          }}
        >
          {cards.length}
        </Box>
      </Box>

      <Typography
        sx={{
          mt: "4px",
          fontSize: "11px",
          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
          color: "hsl(178, 10%, 50%)",
          lineHeight: 1.5,
        }}
      >
        {intl.formatMessage({
          id: "candidate.kanban.closed_hint",
          defaultMessage: "This application is no longer being considered.",
        })}
      </Typography>

      {/* Cards */}
      <Box
        sx={{
          mt: "12px",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: "12px",
        }}
      >
        {cards.slice(0, limit).map((card) => (
          <KanbanCardItem key={`closed-${card.id}`} card={card} readOnly />
        ))}
      </Box>

      {cards.length > limit && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: "12px" }}>
          <Box
            component="button"
            onClick={() => setLimit((prev) => Math.min(prev + CLOSED_PAGE_SIZE, cards.length))}
            sx={{
              px: "16px",
              py: "8px",
              borderRadius: "9999px",
              border: "1px solid hsla(160, 10%, 85%, 0.9)",
              bgcolor: "hsl(0, 0%, 100%)",
              cursor: "pointer",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: "11px",
              color: "hsl(178, 10%, 45%)",
              transition: "all 0.15s",
              "&:hover": { borderColor: "hsla(160, 10%, 70%, 0.9)" },
            }}
          >
            {intl.formatMessage({ id: "candidate.kanban.closed_see_more", defaultMessage: "View more" })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const CandidateKanbanBoard = ({ active = true }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const [columns, setColumns] = useState([]);
  const [cardsByColumnId, setCardsByColumnId] = useState({});
  const [closedCards, setClosedCards] = useState([]);
  const [fetchError, setFetchError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [cardDropTarget, setCardDropTarget] = useState(null);

  // External app modal state
  const [externalModalOpen, setExternalModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [deleteConfirmCard, setDeleteConfirmCard] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // The drop monitor reads the latest cards through a ref so it isn't re-registered on every change.
  const cardsByColumnIdRef = useRef(cardsByColumnId);
  cardsByColumnIdRef.current = cardsByColumnId;

  const notifyActionFailed = useCallback(() => {
    enqueueSnackbar(
      intl.formatMessage({ id: "candidate.dashboard.action_failed", defaultMessage: "Une erreur est survenue. Réessayez." }),
      { variant: "error" }
    );
  }, [enqueueSnackbar, intl]);

  // sh cancels an in-flight request when the same URL is requested again, so only the latest
  // fetch may touch state: a cancelled or superseded one must not flag an error or stop the loader.
  const latestFetchRef = useRef(0);
  const fetchBoard = useCallback((silent = false) => {
    const fetchId = ++latestFetchRef.current;
    if (!silent) setLoading(true);
    setFetchError(false);
    sh.get("candidate/kanban", { authenticity_token: authenticityToken })
      .then((res) => {
        if (fetchId !== latestFetchRef.current) return;
        const data = res.data?.data || {};
        setColumns(data.columns || []);
        setCardsByColumnId(data.cards_by_column_id || {});
        setClosedCards(data.inactive_cards || []);
      })
      .catch((err) => {
        if (axios.isCancel(err) || fetchId !== latestFetchRef.current) return;
        setFetchError(true);
      })
      .finally(() => {
        if (fetchId === latestFetchRef.current) setLoading(false);
      });
  }, [authenticityToken]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  // Tabs stay mounted in the dashboard shell, so a job applied to from another tab
  // wouldn't show up here without a refetch when this tab is re-activated.
  const activatedRef = useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!activatedRef.current) { activatedRef.current = true; return; }
    fetchBoard(true);
  }, [active]);

  // Tell the user, then rethrow so the modal stays open with their input.
  const withFailureNotice = (action) => async (...args) => {
    try {
      await action(...args);
    } catch (err) {
      notifyActionFailed();
      throw err;
    }
  };

  const handleCreateExternal = withFailureNotice(async (form) => {
    await sh.post("candidate/kanban/external_applications", {
      authenticity_token: authenticityToken,
      kanban_column_key: form.kanban_column_key,
      external_job_application: {
        company_name: form.company_name,
        job_title: form.job_title,
        url: form.url,
        location: form.location,
        source: form.source,
      },
    });
    fetchBoard(true);
  });

  const handleEditExternal = withFailureNotice(async (form) => {
    if (!editingCard) return;
    await sh.put(`candidate/kanban/external_applications/${editingCard.subject_id}`, {
      authenticity_token: authenticityToken,
      external_job_application: {
        company_name: form.company_name,
        job_title: form.job_title,
        url: form.url,
        location: form.location,
        source: form.source,
        notes: form.notes,
      },
    });
    setEditingCard(null);
    fetchBoard(true);
  });

  const handleDeleteExternal = async () => {
    if (!deleteConfirmCard || deleting) return;
    setDeleting(true);
    try {
      await sh.delete(`candidate/kanban/external_applications/${deleteConfirmCard.subject_id}`, {
        data: { authenticity_token: authenticityToken },
      });
      setDeleteConfirmCard(null);
      fetchBoard(true);
    } catch {
      notifyActionFailed();
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (card) => {
    setEditingCard(card);
  };

  const openDeleteConfirm = (card) => {
    setDeleteConfirmCard(card);
  };

  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => {
        if (source?.data?.type === "kanban-card") {
          setDraggingCardId(source.data.cardId);
        }
      },
      onDrag: ({ location }) => {
        const target = location?.current?.dropTargets?.[0];
        if (!target) {
          setCardDropTarget(null);
          return;
        }
        const d = target.data;
        if (d?.type === "kanban-card-target") {
          const edge = extractClosestEdge(d);
          setCardDropTarget({
            type: "card",
            columnId: d.columnId,
            index: d.index,
            edge,
          });
        } else if (d?.type === "column") {
          setCardDropTarget({ type: "column", columnId: d.columnId });
        } else {
          setCardDropTarget(null);
        }
      },
      onDrop: ({ source, location }) => {
        setDraggingCardId(null);
        setCardDropTarget(null);

        if (source?.data?.type !== "kanban-card") return;

        const target = location?.current?.dropTargets?.[0];
        if (!target) return;

        const cardId = source.data.cardId;
        const fromColumnId = source.data.fromColumnId;
        const fromColumnKey = source.data.fromColumnKey;
        const d = target.data;

        let toColumnId;
        let toColumnKey;
        let insertIndex;

        if (d?.type === "kanban-card-target") {
          toColumnId = d.columnId;
          toColumnKey = d.columnKey;
          const edge = extractClosestEdge(d);
          insertIndex = edge === "top" ? d.index : d.index + 1;
        } else if (d?.type === "column") {
          toColumnId = d.columnId;
          toColumnKey = d.columnKey;
          insertIndex = (cardsByColumnIdRef.current[toColumnId] || []).length;
        } else {
          return;
        }

        // Block cross-column moves involving the saved column
        if (!canCrossColumn(fromColumnKey, toColumnKey)) return;

        // Optimistic move, restored from this snapshot if the server rejects it
        const previousCardsByColumnId = cardsByColumnIdRef.current;
        const fromCards = [...(previousCardsByColumnId[fromColumnId] || [])];
        const cardIdx = fromCards.findIndex((c) => c.id === cardId);
        if (cardIdx === -1) return;

        const [movedCard] = fromCards.splice(cardIdx, 1);
        const toCards =
          fromColumnId === toColumnId
            ? fromCards
            : [...(previousCardsByColumnId[toColumnId] || [])];

        if (fromColumnId === toColumnId && cardIdx < insertIndex) {
          insertIndex = Math.max(0, insertIndex - 1);
        }
        toCards.splice(insertIndex, 0, { ...movedCard, kanban_column_id: toColumnId });

        setCardsByColumnId((prev) => ({
          ...prev,
          [fromColumnId]: fromCards,
          [toColumnId]: toCards,
        }));

        sh.put(`candidate/kanban/move_card/${cardId}`, {
          authenticity_token: authenticityToken,
          kanban_column_id: toColumnId,
          position: insertIndex,
        })
          .then(() => {
            fetchBoard(true);
          })
          .catch((err) => {
            // Cancelled: the same card was moved again and that newer request takes over.
            if (axios.isCancel(err)) return;
            console.error("Kanban move failed:", err?.response?.status, err?.response?.data || err?.message);
            setCardsByColumnId(previousCardsByColumnId);
            notifyActionFailed();
            fetchBoard(true);
          });
      },
    });
  }, [authenticityToken, fetchBoard, notifyActionFailed]);

  if (loading) {
    return <SquarehubLoader size={56} centered minHeight="50vh" />;
  }

  if (fetchError) {
    return (
      <EmptyState
        icon={Briefcase}
        py="48px"
        title={intl.formatMessage({ id: "candidate.dashboard.load_error_title", defaultMessage: "Impossible de charger les données" })}
        subtitle={intl.formatMessage({ id: "candidate.dashboard.load_error_hint", defaultMessage: "Vérifiez votre connexion internet." })}
        ctaLabel={intl.formatMessage({ id: "candidate.dashboard.retry", defaultMessage: "Réessayer" })}
        onCta={() => fetchBoard()}
      />
    );
  }

  if (columns.length === 0) {
    return (
      <EmptyState
        icon={Briefcase}
        py="48px"
        title={intl.formatMessage({ id: "candidate.kanban.empty_title", defaultMessage: "No applications yet" })}
        subtitle={intl.formatMessage({ id: "candidate.kanban.empty_hint", defaultMessage: "Track every application in one place: add your first one." })}
        ctaLabel={intl.formatMessage({ id: "candidate.kanban.add_external", defaultMessage: "Add application" })}
        onCta={() => setExternalModalOpen(true)}
      />
    );
  }

  return (
    <>
      {/* Full-bleed: the board escapes the dashboard's 1280px main container so the
          columns get the whole viewport. The dashboard root sets `overflow-x: clip`
          to absorb the scrollbar gutter that 100vw counts but 100% doesn't. */}
      <Box
        sx={{
          width: "100vw",
          ml: "calc(50% - 50vw)",
          mr: "calc(50% - 50vw)",
        }}
      >
        {/* Board chrome (banner + Add button) stays aligned with the dashboard's
            main container; only the columns strip below is full-bleed. Mirrors
            the maxWidth + px of the <main> in CandidateDashboard. */}
        <Box sx={{ maxWidth: "1280px", mx: "auto", px: { xs: "16px", sm: "24px", lg: "32px" } }}>
          {/* Instruction banner: the candidate manages this board themselves */}
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: "8px",
            p: "12px 14px", mb: "12px", borderRadius: "12px",
            bgcolor: "hsla(153, 68%, 25%, 0.06)",
            border: "1px solid hsla(153, 68%, 25%, 0.15)",
          }}>
            <Building2 size={15} color="hsl(153, 68%, 25%)" style={{ flexShrink: 0, marginTop: 1 }} />
            <Typography sx={{
              fontSize: "12px", lineHeight: 1.5,
              color: "hsl(178, 10%, 40%)",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            }}>
              {intl.formatMessage({ id: "candidate.kanban.instruction", defaultMessage: "This board is yours to manage: drag your applications across the columns as they progress. Recruiters don't move them for you." })}
            </Typography>
          </Box>

          {/* Board header with Add button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "12px" }}>
            <Box
              component="button"
              onClick={() => setExternalModalOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: "16px",
                py: "10px",
                borderRadius: "12px",
                border: "none",
                bgcolor: "hsl(153, 68%, 25%)",
                color: "hsl(0, 0%, 100%)",
                cursor: "pointer",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "12px",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "hsl(153, 68%, 20%)" },
              }}
            >
              <Plus size={14} />
              {intl.formatMessage({ id: "candidate.kanban.add_external", defaultMessage: "Add application" })}
            </Box>
          </Box>
        </Box>

        {/* Board columns: full-bleed, so they keep the whole viewport */}
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            overflowX: "auto",
            px: { xs: "16px", sm: "24px", lg: "32px" },
            pb: "8px",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "hsla(160, 10%, 70%, 0.4)",
              borderRadius: 3,
            },
          }}
        >
          {columns.map((col) => (
            <KanbanColumnView
              key={col.id}
              column={col}
              cards={cardsByColumnId[col.id] || []}
              draggingCardId={draggingCardId}
              cardDropTarget={cardDropTarget}
              onEditExternal={openEditModal}
              onDeleteExternal={openDeleteConfirm}
            />
          ))}
        </Box>

        {/* Closed applications: back inside the dashboard container, below the board */}
        <Box sx={{ maxWidth: "1280px", mx: "auto", px: { xs: "16px", sm: "24px", lg: "32px" } }}>
          <ClosedApplicationsSection cards={closedCards} />
        </Box>
      </Box>

      {/* Create modal */}
      <ExternalAppModal
        open={externalModalOpen}
        onClose={() => setExternalModalOpen(false)}
        onSubmit={handleCreateExternal}
        columns={columns}
        mode="create"
      />

      {/* Edit modal */}
      <ExternalAppModal
        open={!!editingCard}
        onClose={() => setEditingCard(null)}
        onSubmit={handleEditExternal}
        columns={columns}
        mode="edit"
        initialData={editingCard ? {
          company_name: editingCard.external_job_application?.company_name,
          job_title: editingCard.external_job_application?.job_title,
          url: editingCard.external_job_application?.url,
          location: editingCard.external_job_application?.location,
          source: editingCard.external_job_application?.source,
          notes: editingCard.external_job_application?.notes,
        } : null}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteConfirmCard}
        onClose={() => setDeleteConfirmCard(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: "hsla(0, 65%, 51%, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <AlertTriangle size={18} color="hsl(0, 65%, 51%)" />
            </Box>
            <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
              {intl.formatMessage({ id: "candidate.kanban.external_form.delete_confirm", defaultMessage: "Delete this external application?" })}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Box
              component="button"
              onClick={() => setDeleteConfirmCard(null)}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: "12px",
                borderRadius: "12px",
                border: "1px solid hsla(160, 10%, 90%, 0.6)",
                bgcolor: "hsl(0, 0%, 100%)",
                cursor: "pointer",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "12px",
                color: "hsl(178, 10%, 45%)",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.8)" },
              }}
            >
              {intl.formatMessage({ id: "candidate.kanban.external_form.cancel", defaultMessage: "Cancel" })}
            </Box>
            <Box
              component="button"
              onClick={handleDeleteExternal}
              disabled={deleting}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                py: "12px",
                borderRadius: "12px",
                border: "none",
                bgcolor: "hsl(0, 65%, 51%)",
                color: "hsl(0, 0%, 100%)",
                cursor: "pointer",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "12px",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "hsl(0, 65%, 45%)" },
                "&:disabled": { opacity: 0.6, cursor: "default" },
              }}
            >
              <Trash2 size={12} />
              {intl.formatMessage({ id: "candidate.kanban.external_form.delete", defaultMessage: "Delete" })}
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default CandidateKanbanBoard;
